import { useMemo, useState } from 'react';
import { Crown, Copy, ExternalLink, AlertTriangle, Mail, Users, Plus, Moon } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { InviteAgentDialog } from '@/components/agents/InviteAgentDialog';
import { useWorkspacePresence, isDndActive } from '@/hooks/useWorkspacePresence';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { HelpSectionCompact } from './HelpSectionCompact';
import { getPlanDisplayName } from '@/lib/plans';
import { useWorkspaceUsage } from '@/hooks/usePlanLimits';

function getInitials(firstName: string | null, lastName: string | null): string {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || 'U';
}

function getDisplayName(firstName: string | null, lastName: string | null): string {
  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;
  if (lastName) return lastName;
  return 'Utilisateur';
}

const roleColors: Record<string, string> = {
  owner: 'bg-luminjo-pink',
  admin: 'bg-primary',
  agent: 'bg-luminjo-success',
  viewer: 'bg-muted-foreground',
};

export function InfoSidebar() {
  const { activeWorkspace, members, loading: workspaceLoading, isOwnerOrAdmin } = useWorkspaceContext();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const { usage } = useWorkspaceUsage(activeWorkspace?.id);

  const currentUser = useMemo(
    () =>
      user && profile
        ? {
            id: user.id,
            firstName: profile.first_name,
            lastName: profile.last_name,
            avatarUrl: profile.avatar_url,
            dndEnabled: profile.dnd_enabled,
            dndUntil: profile.dnd_until,
          }
        : null,
    [
      user?.id,
      profile?.first_name,
      profile?.last_name,
      profile?.avatar_url,
      profile?.dnd_enabled,
      profile?.dnd_until,
    ],
  );

  // Real-time presence tracking
  const { connectedAgents } = useWorkspacePresence({
    workspaceId: activeWorkspace?.id,
    currentUser,
  });

  // Check if an agent is online and get their connection info (including DND)
  const getAgentPresence = (userId: string) => {
    const agent = connectedAgents.find(a => a.id === userId);
    if (!agent) return { online: false, onlineAt: null, dndActive: false };
    return { 
      online: true, 
      onlineAt: agent.online_at,
      dndActive: isDndActive(agent.dnd_enabled, agent.dnd_until)
    };
  };

  // Format duration since connection
  const formatOnlineDuration = (onlineAt: string) => {
    const now = new Date();
    const connectedAt = new Date(onlineAt);
    const diffMs = now.getTime() - connectedAt.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    
    if (diffMinutes < 1) return "À l'instant";
    if (diffMinutes < 60) return `Depuis ${diffMinutes} min`;
    if (diffHours < 24) return `Depuis ${diffHours}h${diffMinutes % 60 > 0 ? ` ${diffMinutes % 60}min` : ''}`;
    return `Depuis plus de 24h`;
  };

  const loading = workspaceLoading;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-32" />
          <div className="h-12 bg-muted rounded" />
          <div className="h-20 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!activeWorkspace) {
    return (
      <div className="space-y-6">
        <p className="text-muted-foreground text-sm">Aucun espace sélectionné</p>
      </div>
    );
  }

  const ticketsUsed = usage?.tickets_used || 0;
  const ticketsLimit = usage?.tickets_limit || 10;
  const usagePercentage = ticketsLimit > 0 ? Math.min((ticketsUsed / ticketsLimit) * 100, 100) : 0;
  const planName = getPlanDisplayName(activeWorkspace.plan);
  const isNearLimit = usagePercentage >= 80;
  const isAtLimit = usagePercentage >= 100;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié !`);
  };

  return (
    <div className="space-y-6 p-4 bg-muted/30 rounded-lg border border-border">
      {/* Plan */}
      <div>
        <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          <Crown className="h-3.5 w-3.5" />
          Votre offre
        </div>
        <div className="bg-card rounded-lg p-4 border border-border">
          <p className="font-semibold text-foreground">{planName}</p>
          <Button variant="link" size="sm" className="p-0 h-auto text-primary" asChild>
            <Link to="/offer">Passer à l'offre supérieure</Link>
          </Button>
        </div>
      </div>

      {/* Usage */}
      <div>
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Utilisation
        </div>
        <div className={`bg-card rounded-lg p-4 border space-y-3 ${isAtLimit ? 'border-destructive' : isNearLimit ? 'border-warning' : 'border-border'}`}>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Tickets ce mois</span>
            <span className={`font-medium ${isAtLimit ? 'text-destructive' : isNearLimit ? 'text-warning' : 'text-foreground'}`}>
              {ticketsUsed} / {ticketsLimit}
            </span>
          </div>
          <Progress 
            value={usagePercentage} 
            className={`h-2 ${isAtLimit ? '[&>div]:bg-destructive' : isNearLimit ? '[&>div]:bg-warning' : ''}`} 
          />
          <p className={`text-xs ${isAtLimit ? 'text-destructive' : isNearLimit ? 'text-warning' : 'text-muted-foreground'}`}>
            {ticketsLimit - ticketsUsed} tickets restants
          </p>
        </div>

        {/* Alert when near or at limit */}
        {isNearLimit && (
          <Alert variant={isAtLimit ? 'destructive' : 'default'} className={`mt-3 ${!isAtLimit ? 'border-warning text-warning-foreground [&>svg]:text-warning' : ''}`}>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              {isAtLimit ? (
                <>
                  Limite atteinte ! <Link to="/offer" className="underline font-medium">Passez à l'offre supérieure</Link> pour continuer.
                </>
              ) : (
                <>
                  Vous approchez de votre limite. <Link to="/offer" className="underline font-medium">Augmentez votre quota</Link>.
                </>
              )}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Practical info */}
      <div className="overflow-hidden">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Informations pratiques
        </div>
        <div className="space-y-2">
          {/* Public page URL */}
          {activeWorkspace.public_page_enabled && activeWorkspace.slug && (
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <div 
                  className="flex items-center gap-2 p-2.5 rounded-lg bg-card border border-border hover:border-primary/30 transition-colors cursor-pointer"
                  onClick={() => window.open(`/espace/${activeWorkspace.slug}`, '_blank')}
                >
                  <div className="flex-shrink-0 h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
                    <ExternalLink className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-xs font-medium text-foreground">Page publique</p>
                    <p className="text-[10px] text-muted-foreground truncate">/espace/{activeWorkspace.slug}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 w-7 p-0 flex-shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(`${window.location.origin}/espace/${activeWorkspace.slug}`, 'URL page publique');
                    }}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start" className="max-w-[280px]">
                <p className="text-sm">Portail public où vos clients peuvent consulter la FAQ et soumettre des tickets.</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Support email */}
          {activeWorkspace.slug && (
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <div 
                  className="flex items-center gap-2 p-2.5 rounded-lg bg-card border border-border hover:border-luminjo-success/30 transition-colors cursor-pointer"
                  onClick={() => window.location.href = `mailto:support+${activeWorkspace.slug}@ticket.luminjo.com`}
                >
                  <div className="flex-shrink-0 h-8 w-8 rounded-md bg-luminjo-success/10 flex items-center justify-center">
                    <Mail className="h-3.5 w-3.5 text-luminjo-success" />
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-xs font-medium text-foreground">Email support</p>
                    <p className="text-[10px] text-muted-foreground font-mono truncate">support+{activeWorkspace.slug}@ticket.luminjo.com</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 w-7 p-0 flex-shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(`support+${activeWorkspace.slug}@ticket.luminjo.com`, 'Email support');
                    }}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start" className="max-w-[280px]">
                <p className="text-sm">Les emails envoyés à cette adresse créent automatiquement un ticket dans votre espace.</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Agents Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <Users className="h-3.5 w-3.5" />
            Agents
          </div>
          {isOwnerOrAdmin && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 px-2 text-primary text-xs"
              onClick={() => setInviteDialogOpen(true)}
            >
              <Plus className="h-3 w-3 mr-1" />
              Inviter
            </Button>
          )}
        </div>
        <div className="bg-card rounded-lg p-4 border border-border">
          <div className="flex flex-wrap gap-3">
            {members.map((member) => {
              const presence = getAgentPresence(member.user_id);
              const displayName = getDisplayName(member.profile?.first_name || null, member.profile?.last_name || null);
              
              return (
                <Tooltip key={member.id} delayDuration={200}>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col items-center gap-1 cursor-default">
                      <div className="relative">
                        <Avatar className="h-10 w-10">
                          {member.profile?.avatar_url && (
                            <AvatarImage src={member.profile.avatar_url} />
                          )}
                          <AvatarFallback className={roleColors[member.role] + ' text-white text-xs'}>
                            {getInitials(member.profile?.first_name || null, member.profile?.last_name || null)}
                          </AvatarFallback>
                        </Avatar>
                        {/* Status indicator: DND takes priority over online */}
                        {presence.dndActive ? (
                          <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-card bg-warning flex items-center justify-center">
                            <Moon className="h-2 w-2 text-warning-foreground" />
                          </span>
                        ) : (
                          <span 
                            className={cn(
                              "absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-card transition-colors",
                              presence.online ? "bg-luminjo-success animate-pulse" : "bg-muted-foreground/40"
                            )}
                          />
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground text-center max-w-[60px] truncate">
                        {displayName}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{displayName}</span>
                      {presence.dndActive ? (
                        <span className="text-warning flex items-center gap-1">
                          <Moon className="h-2.5 w-2.5" />
                          Ne pas déranger
                        </span>
                      ) : (
                        <span className={presence.online ? "text-luminjo-success" : "text-muted-foreground"}>
                          {presence.online && presence.onlineAt 
                            ? formatOnlineDuration(presence.onlineAt) 
                            : "Hors ligne"}
                        </span>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
            
            {isOwnerOrAdmin && (
              <button 
                onClick={() => setInviteDialogOpen(true)}
                className="flex flex-col items-center gap-1 opacity-60 hover:opacity-100 transition-opacity"
              >
                <div className="h-10 w-10 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                  <Plus className="h-4 w-4 text-muted-foreground" />
                </div>
                <span className="text-xs text-muted-foreground">Ajouter</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Help Section */}
      <HelpSectionCompact />

      <InviteAgentDialog 
        open={inviteDialogOpen} 
        onOpenChange={setInviteDialogOpen} 
      />
    </div>
  );
}
